import { useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { publicCatalogApi } from '@/features/publicsite/catalog/api/publicCatalogApi';
import { CatalogCard } from '@/features/publicsite/catalog/CatalogCard';
import { CatalogSection } from '@/features/publicsite/catalog/CatalogSection';
import { projectStatusLabel } from '@/features/publicsite/catalog/projectStatus';
import { ProjectDiscussionSection } from '@/features/publicsite/catalog/ProjectDiscussionSection';
import { RESULT_TYPE_KEY } from '@/features/publicsite/catalog/researchResultType';
import { ResultAdoptions } from '@/features/publicsite/catalog/ResultAdoptions';
import { useHashTarget } from '@/features/publicsite/catalog/useHashTarget';
import { formatBudget, formatPeriod } from '@/features/common/projectFormat';
import type { ResearchResult } from '@/features/common/types/ResearchResult';
import type { ResearchProject } from '@/features/publicsite/types/research/ResearchProject';
import { usePublicData, usePublicList } from '@/features/publicsite/usePublicList';
import { publicCatalog } from '@/i18n/dictionaries/publicCatalog';
import { useTranslation } from '@/i18n/useTranslation';
import { usePublicPageTitle } from '@/features/publicsite/usePublicPageTitle';
import { projectPath } from '@/features/publicsite/projectPath';

/**
 * One research project and the work that came out of it.
 *
 * The catalogue listed projects and said "3 rezultate" under each, with nowhere to go and read
 * those three: the count was the end of the trail. A publication on the home page could only point
 * at the project's row in that same list, which told the reader nothing the card they clicked had
 * not already said.
 *
 * Built from the two list endpoints. There is no /public/research/projects/{id} and none is
 * needed — every row from the results endpoint carries `projectId`, so a project's own results are
 * a filter away, and asking the backend for an endpoint the frontend can already answer would be
 * work for nobody's benefit.
 *
 * The frame is CatalogSection, the same one the five catalogue pages use, so the eyebrow, the
 * heading, the grid and the empty state are the ones already defined rather than a second set that
 * looks nearly the same. The only thing this page needed that the component did not have is a
 * narrower empty line, which it now takes as a prop.
 */
export function ResearchProjectPage() {
  const { t, language } = useTranslation(publicCatalog);
  // Named `slug` because that is what the route now carries and what every link in the app
  // builds. A bare id still resolves, though — see the lookup below.
  const { slug: param = '' } = useParams<{ slug: string }>();

  const projects = usePublicList<ResearchProject>(publicCatalogApi.researchProjectsUrl);
  const results = usePublicList<ResearchResult>(publicCatalogApi.researchResultsUrl);
  // One call for the whole page rather than one per card: the counter is what decides whether a
  // result shows an uptake block at all, and asking per result would open a request for each of
  // the dozen cards a project can carry. Seeded to {} so the cards render before it lands — a
  // block that appears a moment later is better than a page that waits on a secondary number.
  const adoptionCounts = usePublicData<Record<string, number>>(
    publicCatalogApi.adoptionCountsUrl,
    {},
  );

  // The home page's publications strip links to one result on this page, by fragment. Called
  // before the early returns below, because a hook cannot be skipped on some renders — and gated
  // on both lists, because the row it has to reach does not exist until they arrive.
  useHashTarget(projects !== null && results !== null);

  // Inside a section-head, where `.head-link` is actually styled — the class is inline-flex there
  // and nowhere else. An earlier version put this in the body with an Icon beside it: Icon sets no
  // width or height by design, `.link-arrow` is defined in no stylesheet, and the arrow stretched
  // to half the page.
  const backLink = (
    <Link className="head-link" to="/cercetare">
      {t('researchBack')}
    </Link>
  );

  // Resolved before the early returns below rather than after them: a hook cannot be called
  // conditionally, and this page has three exits (loading, unknown id, found). Mirrors the
  // "found" branch's own value (CatalogSection sets the same `project.title` as its `heading`
  // prop) so the two agree rather than one clobbering the other on the render where both fire.
  // Slug first, id as a fallback — the same rule EventDetailPage applies, and for the same
  // reason: /cercetare/<uuid> links were handed out before the slug column existed, and they are
  // in people's bookmarks and mail. Matching both means those keep working instead of turning
  // into a not-found page on the day the slugs shipped.
  const project = projects?.find((p) => p.slug === param || p.id === param) ?? null;
  usePublicPageTitle(
    projects === null || results === null ? null : (project?.title ?? t('researchNotFound')),
  );

  // A link shared as /cercetare/<uuid> keeps working (the lookup above accepts both), but it does
  // not stay a uuid: the address bar is rewritten to the slug, so what the reader copies from
  // here onward is the readable form. `replace` rather than `push`, or Back would return to the
  // uuid and redirect forward again, trapping the reader on this page.
  //
  // Safe to run in an effect keyed on the param: usePublicList is keyed on its URL, which is a
  // constant here, so changing :slug re-renders without re-fetching anything.
  const navigate = useNavigate();
  const { hash, search } = useLocation();
  useEffect(() => {
    if (project && project.slug && param !== project.slug) {
      // Carried across, not dropped. The one link shape that most needs the id fallback is the
      // home page's deep link into a single publication — /cercetare/<uuid>#rezultat-<id> — and
      // rewriting to a bare path would strip the anchor useHashTarget exists to serve, landing
      // the reader at the top of the project instead of on the result they clicked.
      navigate(`${projectPath(project)}${search}${hash}`, { replace: true });
    }
  }, [project, param, navigate, hash, search]);

  // Both lists, not either: deciding "no such project" before the projects have arrived would
  // flash the not-found page over a project that is about to render.
  if (projects === null || results === null) {
    return (
      <section className="container section">
        <p className="empty-state">{t('loading')}</p>
      </section>
    );
  }

  if (!project) {
    // Not routed through CatalogSection: there is no list here to be empty, and passing it an
    // empty one would print "no records yet" under a heading that has already said what happened.
    return (
      <section className="container section">
        <p className="eyebrow">{t('eyebrowCatalog')}</p>
        <h1>{t('researchNotFound')}</h1>
        <p>{backLink}</p>
      </section>
    );
  }

  const period = formatPeriod(project.startDate, project.endDate, language);

  return (
    <CatalogSection
      eyebrow={t('eyebrowCatalog')}
      heading={project.title}
      // The institution, as a way to reach it. The name was printed as plain text under every
      // project, which made the obvious question — who are these people, what else do they work
      // on — a dead end. The server started returning `organizationId` for exactly this.
      lead={
        <Link to={`/cercetare/organizatii/${project.organizationId}`}>{project.organization}</Link>
      }
      items={results.filter((r) => r.projectId === project.id)}
      // Narrower than "no records yet, come back later": a project may simply never publish any,
      // and a result reaches the public site on its own approval, not its project's.
      emptyMessage={t('researchNoResultsYet')}
      headLink={backLink}
      renderCard={(r) => (
        <CatalogCard
          key={r.id}
          // The anchor the publications strip aims at, so a click lands on the publication that
          // was clicked rather than on the list containing it.
          id={`rezultat-${r.id}`}
          badge={r.year != null ? String(r.year) : t('researchNoYear')}
          title={r.title}
          subtitle={r.journal}
        >
          {r.description && <p>{r.description}</p>}
          <p className="muted">{t(RESULT_TYPE_KEY[r.type])}</p>
          {/*
            Only when the server says there is one. `document` is absent both when nothing was
            attached and when the file has not been opened to the public, and the two are the same
            thing to a visitor: no block at all, rather than a button that answers 403.

            The href is the endpoint, not a media URL assembled here — it is the only place that
            re-checks the result is still published before handing the file over.
          */}
          {/* Not gated on the counts having arrived: the component renders nothing for a zero,
              and `{}` before the response lands reads as zero for every card — so a block
              appears when there is one to show and never flashes an empty one. */}
          <ResultAdoptions resultId={r.id} count={adoptionCounts?.[r.id] ?? 0} />
          {r.document && (
            <div className="card__foot">
              <span className="muted">
                {r.document.name} · {(r.document.sizeBytes / 1048576).toFixed(1)} MB
              </span>
              <a className="btn btn--ghost" href={publicCatalogApi.resultDownloadUrl(r.id)}>
                {t('documentDownload')}
              </a>
            </div>
          )}
        </CatalogCard>
      )}
    >
      <p>
        <span className="badge">{projectStatusLabel(t, project.status)}</span>
      </p>
      <p>{project.summary}</p>

      {/*
        The registry FR-020 asks for, printed one block per field. Every block is conditional: an
        institution registers a project before it has obtained anything, and often before a budget
        is agreed, so a heading over an empty line would claim it answered when it was never asked
        twice. Same rule the institution's own profile page follows.
      */}
      {period && (
        <div className="org-profile__field">
          <h2>{t('projectPeriodHeading')}</h2>
          <p>{period}</p>
        </div>
      )}
      {project.budget != null && (
        <div className="org-profile__field">
          <h2>{t('projectBudgetHeading')}</h2>
          <p>{formatBudget(project.budget)}</p>
        </div>
      )}
      {project.partners.length > 0 && (
        <div className="org-profile__field">
          <h2>{t('projectPartnersHeading')}</h2>
          <ul>
            {project.partners.map((p) => (
              // The name is what identifies the row; the role qualifies it and is null when the
              // institution named a partner without saying what it does on the project.
              <li key={p.name}>{p.role ? `${p.name} — ${p.role}` : p.name}</li>
            ))}
          </ul>
        </div>
      )}
      {project.funding.length > 0 && (
        <div className="org-profile__field">
          <h2>{t('projectFundingHeading')}</h2>
          <ul>
            {project.funding.map((f) => (
              <li key={f.source}>
                {f.amount != null ? `${f.source} — ${formatBudget(f.amount)}` : f.source}
              </li>
            ))}
          </ul>
        </div>
      )}
      {project.objectives && (
        <div className="org-profile__field">
          <h2>{t('projectObjectivesHeading')}</h2>
          <p>{project.objectives}</p>
        </div>
      )}
      {project.resultsExpected && (
        <div className="org-profile__field">
          <h2>{t('projectResultsExpectedHeading')}</h2>
          <p>{project.resultsExpected}</p>
        </div>
      )}
      {project.resultsObtained && (
        <div className="org-profile__field">
          <h2>{t('projectResultsActualHeading')}</h2>
          <p>{project.resultsObtained}</p>
        </div>
      )}
      {/* Wrapped, not bare: global.css sizes section headings through `.section-head h2`, so an h2
          sitting directly in the section falls back to the generic rule and renders at 18px —
          a bold paragraph where every other heading on the site is 32px. */}
      {/* Above the results heading rather than after the grid: CatalogSection renders `children`
          before the item grid, so anything placed last here still lands above the results. The
          discussion is about the project as a whole, which is what this half of the page is. */}
      <ProjectDiscussionSection projectId={project.id} />

      <div className="section-head">
        <div>
          <h2>{t('researchResultsHeading')}</h2>
        </div>
      </div>
    </CatalogSection>
  );
}
